/**
 * Track0tic Platform Admin Layout — Phase 7: five collapsible workflow groups, per-admin open state in localStorage,
 * menu search, BFSI-only items (Consent grants, Compliance) shown only when a BFSI tenant exists.
 * Dark sidebar #1B1D1C, accent #0B6B55, bg #F4F3EF, IBM Plex fonts.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { adminGet } from './adminApi';
import { cn } from '@/lib/utils';
import {
  AlertTriangle, LayoutDashboard, Cpu, ClipboardCheck, QrCode, Building2, Share2, Truck, BookOpen, Route, Users,
  ShieldCheck, BarChart3, LogOut, CheckSquare, Thermometer, PackagePlus, Tags, ChevronDown, Search, Database,
} from 'lucide-react';

export const GROUPS = [
  { id: 'devices', label: 'Devices', items: [
    { id: 'receive', label: 'Receive devices', icon: PackagePlus, path: '/admin/devices/receive', menu: 'device-registration' },
    { id: 'inventory', label: 'Inventory', icon: Cpu, path: '/admin/devices', menu: 'devices', exact: true },
    { id: 'qc', label: 'QC and firmware', icon: ClipboardCheck, path: '/admin/qc', menu: 'qc' },
    { id: 'device-labels', label: 'Device labels', icon: QrCode, path: '/admin/device-labels', menu: 'device-registration' },
  ] },
  { id: 'tenants', label: 'Tenants', items: [
    { id: 'tenants', label: 'Tenants', icon: Building2, path: '/admin/tenants', menu: 'customers' },
    { id: 'users', label: 'Users and roles', icon: Users, path: '/admin/users', menu: 'customers' },
    { id: 'journeys', label: 'Journey templates', icon: Route, path: '/admin/journeys', menu: 'journeys' },
    { id: 'consent-grants', label: 'Consent grants', icon: ShieldCheck, path: '/admin/consent-grants', menu: 'consent-grants', bfsiOnly: true },
  ] },
  { id: 'supply', label: 'Sale & supply', items: [
    { id: 'allocation', label: 'Allocate devices', icon: Share2, path: '/admin/allocation', menu: 'allocation' },
    { id: 'issue-passports', label: 'Issue passports', icon: Tags, path: '/admin/issue-passports', menu: 'qr-passports' },
    { id: 'qr-profiles', label: 'Temperature profiles', icon: Thermometer, path: '/admin/qr-profiles', menu: 'qr-passports' },
  ] },
  { id: 'operations', label: 'Operations', items: [
    { id: 'trips', label: 'Trips', icon: Truck, path: '/admin/trips', menu: 'journeys' },
    { id: 'alerts', label: 'Alerts', icon: AlertTriangle, path: '/admin/alerts', menu: 'qr-exceptions' },
    { id: 'approvals', label: 'Approvals', icon: CheckSquare, path: '/admin/approvals', menu: 'approvals' },
    { id: 'compliance', label: 'Compliance', icon: ShieldCheck, path: '/admin/compliance', menu: 'security', bfsiOnly: true },
  ] },
  { id: 'admin', label: 'Admin', items: [
    { id: 'staff-users', label: 'Staff users', icon: Users, path: '/admin/staff-users', menu: 'staff-users' },
    { id: 'reports', label: 'Reports', icon: BarChart3, path: '/admin/reports', menu: 'reports' },
    { id: 'audit-log', label: 'Audit log', icon: BookOpen, path: '/admin/audit-log', menu: 'audit-log' },
    { id: 'settings-ref', label: 'Reference data', icon: Database, path: '/admin/settings-ref', menu: 'settings-ref' },
  ] },
];
const DASHBOARD = { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard', menu: 'dashboard' };
// Paths that stay reachable (redirect targets / detail pages) but are not sidebar items: map them to the group that should open.
const EXTRA_PATHS = { '/admin/passports': 'supply', '/admin/customers': 'tenants', '/admin/qr-labels': 'devices', '/admin/qr-passports': 'supply', '/admin/qr-exceptions': 'operations', '/admin/staff-approvals': 'operations' };

const navAllowed = (me, item) => !me?.menu || me.menu.includes(item.menu);
const isBfsi = (c) => (c.vertical_code || '').toUpperCase() === 'BFSI' || ['BANK', 'BANK_P2'].includes((c.profile_type || '').toUpperCase());
const storeKey = (uid) => `trk-admin-nav-${uid || 'anon'}`;
const DEFAULT_OPEN = { devices: true };

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useTrackOticAuth();
  const [me, setMe] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [hasBfsi, setHasBfsi] = useState(false);
  const [open, setOpen] = useState(DEFAULT_OPEN);
  const [query, setQuery] = useState('');

  // Keep the current page's group open (also after a reload).
  const activeGroup = useMemo(() => {
    const p = location.pathname;
    const hit = GROUPS.find(g => g.items.some(i => i.exact ? (p === i.path || /^\/admin\/devices\/[^/]+$/.test(p) && !p.endsWith('/receive')) : p.startsWith(i.path)));
    return hit?.id || Object.entries(EXTRA_PATHS).find(([k]) => p.startsWith(k))?.[1] || null;
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    try { setOpen({ ...DEFAULT_OPEN, ...JSON.parse(localStorage.getItem(storeKey(user.id)) || '{}'), ...(activeGroup ? { [activeGroup]: true } : {}) }); } catch { setOpen({ ...DEFAULT_OPEN, ...(activeGroup ? { [activeGroup]: true } : {}) }); }   // current page's group always opens, even if stored closed
    const poll = () => Promise.all([adminGet('/bfsi-approvals?status=PENDING').catch(() => []), adminGet('/approvals?status=pending').catch(() => [])])
      .then(([bfsi, staff]) => setPendingApprovals((bfsi?.length || 0) + (staff?.length || 0)));
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- activeGroup is merged in once on load; path changes are handled below

  useEffect(() => { if (!loading && !user) navigate('/', { replace: true }); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    adminGet('/me').then(setMe).catch((e) => {
      if (e?.status === 403 || e?.status === 401) { navigate('/ops', { replace: true }); return; }   // tenant users never see the admin shell
      setMe({ role: 'platform_admin' });
    });
    adminGet('/customers-lite').then(list => setHasBfsi(list.some(isBfsi))).catch(() => {});
  }, [user]);

  const role = me ? (me.legacy_role || me.role) : null;
  const roleCode = me?.role_code || null;

  const groups = useMemo(() => role ? GROUPS.map(g => ({ ...g, items: g.items.filter(i => navAllowed(me, i) && (!i.bfsiOnly || hasBfsi)) })).filter(g => g.items.length) : [], [role, me, hasBfsi]);
  const allowedPaths = useMemo(() => [DASHBOARD, ...groups.flatMap(g => g.items)].filter(i => navAllowed(me, i)).map(i => i.path), [groups, me]);

  // Redirect if the current path is not permitted for this role (matrix from the server); legacy paths resolve through their redirects first.
  useEffect(() => {
    if (!me?.menu) return;
    const p = location.pathname;
    const legacy = Object.keys(EXTRA_PATHS).some(k => p.startsWith(k));
    if (!legacy && !allowedPaths.some(a => p.startsWith(a))) navigate(allowedPaths[1] || allowedPaths[0] || '/admin/devices', { replace: true });
  }, [me, location.pathname, navigate, allowedPaths]);


  useEffect(() => { if (activeGroup) setOpen(o => (o[activeGroup] ? o : { ...o, [activeGroup]: true })); }, [activeGroup]);   // current page's group is always open on load
  const toggle = (gid) => setOpen(o => { const n = { ...o, [gid]: !(o[gid] ?? false) }; localStorage.setItem(storeKey(user?.id), JSON.stringify(n)); return n; });
  const isActive = (item) => item.exact ? (location.pathname === item.path || (/^\/admin\/devices\/[^/]+$/.test(location.pathname) && !location.pathname.endsWith('/receive')))
    : location.pathname.startsWith(item.path);
  const q = query.trim().toLowerCase();
  const matches = (item) => !q || item.label.toLowerCase().includes(q);

  const NavItem = ({ item, nested }) => (
    <Link to={item.path} data-testid={`admin-nav-${item.id}`}
      className={cn('flex items-center gap-3 py-2 rounded-md text-sm transition-colors', nested ? 'pl-7 pr-3' : 'px-3',
        isActive(item) ? 'text-white font-medium' : 'text-[#b8bdb9] hover:text-white hover:bg-white/5')}
      style={isActive(item) ? { background: '#0B6B55' } : undefined}>
      <item.icon className="w-4 h-4 shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.id === 'approvals' && pendingApprovals > 0 && (
        <span data-testid="approvals-pending-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#E4572E', color: 'white' }}>{pendingApprovals}</span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen flex" style={{ background: '#F4F3EF', fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="admin-layout">
      <aside className="w-60 shrink-0 flex flex-col" style={{ background: '#1B1D1C' }}>
        <div className="px-5 pt-5 pb-3 flex items-baseline gap-2">
          <span className="text-white text-xl font-bold tracking-tight">Track<span style={{ color: '#E4572E' }}>0</span>tic</span>
          <span className="text-xs" style={{ color: '#8a8f8c' }}>admin</span>
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5" style={{ color: '#8a8f8c' }} />
            <input data-testid="admin-nav-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search menu"
              className="w-full h-8 pl-8 pr-2 rounded-md text-sm bg-white/5 text-white placeholder:text-[#8a8f8c] outline-none focus:bg-white/10" />
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto pb-3" data-testid="admin-nav">
          {role && matches(DASHBOARD) && navAllowed(me, DASHBOARD) && <NavItem item={DASHBOARD} />}
          {groups.map((g, idx) => {
            const items = g.items.filter(matches);
            if (!items.length) return null;
            const expanded = q ? true : (open[g.id] ?? (g.id === activeGroup));
            return (
              <div key={g.id} className="pt-2" data-testid={`admin-nav-group-${g.id}`}>
                <button type="button" onClick={() => toggle(g.id)} data-testid={`admin-nav-group-toggle-${g.id}`} aria-expanded={expanded}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md hover:bg-white/5"
                  style={{ color: g.id === activeGroup ? '#8fd3bf' : '#8a8f8c' }}>
                  <span className="w-4 text-center" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{idx + 1}</span>
                  <span className="flex-1 text-left">{g.label}</span>
                  <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', expanded ? '' : '-rotate-90')} />
                </button>
                {expanded && <div className="space-y-0.5 mt-0.5">{items.map(i => <NavItem key={i.id} item={i} nested />)}</div>}
              </div>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between">
          <div className="text-xs" style={{ color: '#8a8f8c' }}>
            {user?.full_name || 'Admin'}
            {roleCode && roleCode !== 'ADMIN' && <span className="block" style={{ color: '#0B6B55' }} data-testid="admin-role-label">{roleCode.replace(/_/g, ' ')}</span>}
          </div>
          <button onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout-btn" className="text-[#b8bdb9] hover:text-white transition-colors p-1.5 rounded">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto">
        <Outlet context={{ role, roleCode, me, hasBfsi }} />
      </main>
    </div>
  );
}
